import axios from 'axios';
import {TRANSACTIONS} from '@advisor/util/constants';
import Service from '@advisor/util/Service';
import PII from '@advisor/util/PII';
// eslint-disable-next-line import/no-cycle
import {generateServiceProxyOptions} from '@advisor/util/advisorUtils';

const {MS_BASE, ADMIN_TOKEN_ROUTE} = Service;

// this is a simple proxy to our microservice layer
// that doesn't transform the request AT ALL
export default class ProxyApi {
  /* Dashboard: */
  // GET: /v1/custodian/moneyTransferRequest?riaUserId={riaUserId}&&componentId={componentId}&&startDate={startDate}&&endDate={endDate}&&clientFilter={clientFilter}&&limit={limit}&&offset={offset}
  static moneyTransferRequest(payload) {
    return axios.get(
      `${MS_BASE}/v1/custodian/moneyTransferRequest?riaUserId=${payload.riaUserId}&componentId=${payload.componentId}&startDate=${payload.startDate}&endDate=${payload.endDate}&clientFilter=${payload.clientFilter}&limit=${payload.limit}&offset=${payload.offset}`
    );
  }

  // GET: /v1/advisor/riaRoleAccessComponents
  static riaRoleAccessComponents() {
    return axios.get(`${MS_BASE}/v1/advisor/riaRoleAccessComponents`);
  }

  // GET: /v1/advisor/riaUsers/{riaUserId}
  static getRiaUserbyRiaUserId(riaUserId) {
    return axios.get(`${MS_BASE}/v1/advisor/riaUsers/${riaUserId}`);
  }

  // GET: /v1/advisor/riaUsers/{riaUserId}
  static getRiaUserbyRiaUserIdAT(riaUserId) {
    return axios.get(`${MS_BASE}/${ADMIN_TOKEN_ROUTE}/v1/advisor/riaUsers/${riaUserId}`);
  }

  // GET: /v1/advisor/riaUserIds/{riaId}
  static getRiaUserIds(riaId) {
    return axios.get(`${MS_BASE}/v1/advisor/riaUserIds?riaId=${riaId}`);
  }

  // GET: /v1/advisor/riaUsers/{riaId}
  static getRiaUsers(riaId) {
    return axios.get(`${MS_BASE}/v1/advisor/riaUsers?riaId=${riaId}`);
  }

  // POST: /v1/orchestrate/riaUserIdentityVerification {riaUserId, emailCode, email}
  static confirmEmail(payload) {
    const defaults = {
      baseUrl: `${MS_BASE}/${ADMIN_TOKEN_ROUTE}`,
    };

    const {baseUrl, options} = generateServiceProxyOptions(defaults, true);

    return axios.post(
      `${baseUrl}/v1/orchestrate/riaUserIdentityVerification?riaUserId=${payload.riaUserId}`,
      {
        emailVerificationCode: payload.emailCode,
      },
      options
    );
  }

  static addAdvisorUser(payload) {
    return axios.post(`${MS_BASE}/v1/advisor/riaUsers`, payload);
  }

  static editAdvisorUser(payload) {
    return axios.post(`${MS_BASE}/v1/advisor/riaUsers`, payload);
  }

  // POST: /v1/orchestrate/emailWorkflows
  static verifyEmail(payload) {
    const defaults = {
      baseUrl: `${MS_BASE}/${ADMIN_TOKEN_ROUTE}`,
    };

    const {baseUrl, options} = generateServiceProxyOptions(defaults, true);

    return axios.post(`${baseUrl}/v1/orchestrate/emailWorkflows`, payload, options);
  }

  // POST: /v1/orchestrate/riaUserWorkflows
  static createOAthUser(payload) {
    const defaults = {
      baseUrl: `${MS_BASE}/${ADMIN_TOKEN_ROUTE}`,
    };
    const {baseUrl, options} = generateServiceProxyOptions(defaults, true);

    return axios.post(`${baseUrl}/v1/orchestrate/riaUserWorkflows`, payload, options);
  }

  // DELETE: /v1/advisor/riaUsers/{riaUserId}
  static deleteRiaUser(riaUserId) {
    return axios.delete(`${MS_BASE}/v1/advisor/riaUsers/${riaUserId}`);
  }

  // GET: /v1/auth/emailAvailability/{email}/ria_user/{riaId}
  static getEmailAvailability(payload) {
    return axios.get(
      `${MS_BASE}/${ADMIN_TOKEN_ROUTE}/v1/auth/emailAvailability/${payload.email}/ria_user?riaId=${payload.riaId}`
    );
  }

  // request email verification link for new advisor user
  // / POST /v1/orchestrate/riaUserWorkflows/{email}/ria_user/{riaId}
  static riaUserAuthentication(payload) {
    const defaults = {
      baseUrl: `${MS_BASE}/${ADMIN_TOKEN_ROUTE}`,
    };
    const {baseUrl, options} = generateServiceProxyOptions(defaults, true);

    return axios.post(`${baseUrl}/v1/orchestrate/riaUserWorkflows`, payload, options);
  }

  // GET: /v1/advisor/riaGroupsPrivileges
  static riaGroupsPrivileges() {
    return axios.get(`${MS_BASE}/v1/advisor/riaGroupsPrivileges`);
  }

  /* Client Details: */
  // GET: /v1/client/clUsers/riaUserId/{limit}/{offset}/{clientFilter}/{fullyPopulated}
  static getClients(payload) {
    const clientFilter = payload.clientFilter ? `&clientFilter=${payload.clientFilter}` : ``;
    const search = payload.searchValue ? `&searchValue=${payload.searchValue}` : ``;
    const sortOrder = payload.sortOrder ? `&sortOrder=${payload.sortOrder}` : ``;
    const sortField = payload.sortOrder ? `&sortField=${payload.sortField}` : ``;

    return axios.get(
      `${MS_BASE}/v1/client/clUsers?riaUserId=${payload.riaUserId}&limit=${payload.limit}${sortOrder}${sortField}${clientFilter}&offset=${payload.offset}&fullyPopulated=${payload.fullyPopulated}${search}`
    );
  }

  static getClientsSummary(payload) {
    return axios.get(`${MS_BASE}/v1/advisor/riaUserOverview?riaUserId=${payload.riaUserId}`);
  }

  static getAdvisors(payload) {
    return axios.get(`${MS_BASE}/v1/advisor/riaUsers?riaId=${payload.riaId}`);
  }

  static updateClient(payload) {
    return axios.post(`${MS_BASE}/v1/client/clUsers`, payload);
  }

  // GET: /v1/client/clUsers/:clientId?include=goals,clientAccounts
  static getClientWithSubResources(payload) {
    const subResourceFilter = payload.subResources ? `&include=${payload.subResources}` : ``;
    const piiProps = payload.piiProps && Array.isArray(payload.piiProps) ? payload.piiProps : [];
    const includeFields = piiProps.length > 0 ? `&includeFields=${PII.toUrlString(piiProps)}` : '';
    const region1 = new Intl.DateTimeFormat();
    const areazone = region1.resolvedOptions();

    return axios.get(
      `${MS_BASE}/v1/client/clUsers/${payload.id}?timeZone=${areazone.timeZone}${subResourceFilter}${includeFields}`
    );
  }

  // GET: v1/client/clUsersHouseholdGoals/{cluserId}?include=goals,households&timeZone={timeZone}&riaComponent=CLIENTS
  static getClientHouseholdGoals(payload) {
    const subResourceFilter = payload.subResources ? `&include=${payload.subResources}` : ``;
    const region1 = new Intl.DateTimeFormat();
    const areazone = region1.resolvedOptions();

    return axios.get(
      `${MS_BASE}/v1/client/clUsersHouseholdGoals/${payload.id}?timeZone=${areazone.timeZone}${subResourceFilter}`
    );
  }

  // GET: /v1/client/document/{clientId}
  static getClientDocuments(payload) {
    return axios.get(`${MS_BASE}/v1/client/document/${payload.clientId}`);
  }

  // v1/client/clUsers'
  static updateClientAccess(payload) {
    return axios.put(`${MS_BASE}/v1/client/clUsers`, payload);
  }

  // GET: /v1/client/document/download/{docId}
  static downloadDocument(payload) {
    return axios.get(`${MS_BASE}/v1/client/document/download/${payload.docId}`);
  }

  // GET: /v1/client/document/preview/{docId}
  static previewDocument(payload) {
    return axios.get(`${MS_BASE}/v1/client/document/preview/${payload.docId}`);
  }

  // GET: /v1/client/document/delete/{riaId/{clientId}/{docId}}/
  static deleteDocument(payload) {
    return axios.get(`${MS_BASE}/v1/client/document/delete/${payload.riaId}/${payload.clientId}/${payload.docId}`);
  }

  // GET: /v1/client/goals?riaUserId=
  static getGoals(payload) {
    return axios.get(
      `${MS_BASE}/v1/client/goals?riaUserId=${payload.riaUserId}&limit=${payload.limit}&offset=${payload.offSet}&fullyPopulated=true&clientFilter=${payload.filter}`
    );
  }

  // GET: v1/client/accounts/?goalId=
  static getGoalAccounts(payload) {
    return axios.get(`${MS_BASE}/v1/client/clientAccountsByGoalId?goalId=${payload.goalId}`);
  }

  // v1/client/clUsers/{riaId}/{clientId}/ssn
  static getClientFullSsn(payload) {
    return axios.get(`${MS_BASE}/v1/client/clientSSNs/${payload.clientId}`);
  }

  // POST: /v1/orchestrate/emailWorkflows/
  static emailChangeVerification(payload) {
    const defaults = {
      baseUrl: `${MS_BASE}/${ADMIN_TOKEN_ROUTE}`,
    };

    const {baseUrl, options} = generateServiceProxyOptions(defaults, true);

    return axios.post(`${baseUrl}/v1/orchestrate/emailWorkflows`, payload, options);
  }

  // POST: /v1/advisor/riaUsers/
  static saveRiaUser(payload) {
    return axios.post(`${MS_BASE}/v1/advisor/riaUsers`, payload);
  }

  // v1/advisor/advisorFilters
  static getAdvisorFilter(payload) {
    return axios.get(`${MS_BASE}/v1/advisor/advisorFilters?filterGroupId=${payload.riaGroupId}`);
  }

  static changePassword(payload) {
    return axios.put(`${MS_BASE}/${ADMIN_TOKEN_ROUTE}/v1/uaa/password`, payload);
  }

  static getPlatformAccess(params) {
    const sideBar = params.isSideBarMenu ? `&isSidebarMenu=true` : '';

    return axios.get(`${MS_BASE}/v1/advisor/riaRoleAccessComponents?riaUserId=${params.riaUserId}${sideBar}`);
  }

  // POST: v1/client/document/uploadbybase64
  static documentUpload(payload) {
    return axios.post(`${MS_BASE}/v1/client/document/uploadbybase64`, payload);
  }

  // POST: v1/client/clientAccounts
  static assignGoalToAccount(payload) {
    return axios.post(`${MS_BASE}/v1/client/clientAccounts`, payload);
  }

  static updateClientRoboStatus(params) {
    return axios.put(`${MS_BASE}/v1/client/user/${params.clUserId}`, params.payload);
  }

  static accountUpdateRoboAndRiskScore(params) {
    return axios.post(`${MS_BASE}/v1/client/clientAccounts`, params.accPayload);
  }

  // GET: /v1/client/houseHolds?riaUserId={riaUserId}&limit={limit}&offset={offSet}&componentId={componentId}
  static getHouseholdsList(params) {
    return axios.get(
      `${MS_BASE}/v1/client/houseHolds?riaUserId=${params.riaUserId}&limit=${params.limit}&offset=${params.offSet}`
    );
  }

  // GET: /v1/client/clUsers/{clUserId}/monetaryTransactions
  static monetaryTransactions(params) {
    const safeLimit = params.limit < 0 ? TRANSACTIONS.DEFAULTS.LIMIT : params.limit;

    return axios.get(
      `${MS_BASE}/v1/client/clUsers/${params.clUserId}/monetaryTransactions?offset=${params.offset}&limit=${safeLimit}&transactionCodes=${params.transactionCodes}`
    );
  }

  // GET: /v1/client/clUsers/{riaUserId}/monetaryTransactions
  static historyOfTransactions(params) {
    const safeLimit = params.limit < 0 ? TRANSACTIONS.DEFAULTS.LIMIT : params.limit;

    return axios.get(
      `${MS_BASE}/v1/client/monetaryTransactions/?riaUserId=${params.riaUserId}&offset=${params.offset}&limit=${safeLimit}&transactionCodes=${params.transactionCodes}`
    );
  }

  static setPrivateLabelData(params) {
    return axios.post(`${MS_BASE}/v1/advisor/riaLabelSettings`, params);
  }

  static uploadFileToS3(params) {
    return axios.post(`${MS_BASE}/v1/advisor/riaLabelSettings/docvault?componentId=${params.componentId}`, params);
  }

  static sendClientRegistrationMail(params) {
    return axios.post(`${MS_BASE}/v1/orchestrate/clUserWorkflows`, params);
  }

  static getFileFromS3(params) {
    return axios.get(`${MS_BASE}/v1/advisor/riaLabelSettings/docvault?filePath=${params.filePath}`);
  }

  static getWorkflowDocuments(params) {
    return axios.get(`${MS_BASE}/v1/advisor/workFlowDocuments?riaId=${params.riaId}`);
  }

  static getAccountTypeList(payload) {
    return axios.get(`${MS_BASE}/v1/client/accountTypes?clUserId=${payload.clUserId}`, payload);
  }

  /**
   * This will update the account details to custodian
   * @param {requestId} payload
   */
  static getAccountRequestDetails(payload) {
    return axios.get(`${MS_BASE}/v1/custodian/accountRequests/${payload.requestId}`, payload);
  }

  /**
   * This API is used for associating/ creating goals for given client.
   * @param {goalId} payload
   */
  static disassociateGoalWithClient(payload) {
    return axios.post(`${MS_BASE}/v1/client/goals/${payload.goalId}`, {
      status: 0,
    });
  }

  /**
   * This returns all US states
   * @param {Object} payload
   */

  static getUsStates() {
    return axios.get(`${MS_BASE}/v1/custodian/usStates`);
  }

  /**
   * This returns shared repcodes
   * @param {riaId} payload
   */
  static getSharedRepCodes(payload) {
    return axios.get(`${MS_BASE}/v1/custodian/sharedRepCodes?riaId=${payload.riaId}&repCodeStatus=1`);
  }

  /**
   * This returns client employment statuses
   * @param {custodian} payload
   */
  static getClientProfessionTypes(payload) {
    return axios.get(`${MS_BASE}/v1/custodian/clientProfessionTypes?custodian=${payload.custodian}`);
  }

  static getClientGoals(payload) {
    return axios.get(`${MS_BASE}/v1/client/getgoallist/${payload.clUserId}`);
  }

  static getRelationships() {
    return axios.get(`${MS_BASE}/v1/custodian/clientRelationships`);
  }

  static getCitizenships() {
    return axios.get(`${MS_BASE}/v1/custodian/citizenships`);
  }

  static getNetWorthIntervals() {
    return axios.get(`${MS_BASE}/v1/custodian/clientNetWorths`);
  }

  static getIncomeIntervals() {
    return axios.get(`${MS_BASE}/v1/custodian/clientIncomeIntervals`);
  }

  /**
   * Get existing client user id
   * @param {clUserId} params client id
   */
  static getClientTrustedContact(params) {
    return axios.get(`${MS_BASE}/v1/custodian/custodianAccounts/trustedContact/?clUserId=${params.clUserId}`);
  }

  /**
   * Get account addiotnal info header; contains cash sweep vehicles and other headers data
   */
  static getAccountAdditionalHeadersInfo() {
    return axios.get(`${MS_BASE}/v1/custodian/additionalInfoHeader`);
  }

  /**
   * This will delete the trusted contact of given requestId
   * @param {requestId} payload This payload should contain requestId
   */
  static deleteTrustedContact(payload) {
    return axios.delete(`${MS_BASE}/v1/custodian/custodianAccounts/trustedContact?accountRequestId=${payload.requestId}`);
  }

  static getAdvisorInvoiceDetails(payload) {
    const defaults = {
      baseUrl: MS_BASE,
    };

    const {baseUrl, options} = generateServiceProxyOptions(defaults, true);

    return axios.get(`${baseUrl}/v1/advisor/riaFirm/${payload.riaId}`, options);
  }

  static convertHtmlToPdf(payload) {
    return axios.post(`${MS_BASE}/v1/convert/htmlToPdf`, payload);
  }

  static enquireJobStatus(payload) {
    return axios.get(`${MS_BASE}/v1/jobtracker/scheduledjob/${payload.jobId}`);
  }

  static deActivateClient(payload) {
    return axios.put(`${MS_BASE}/v1/client/deActiveUser`, payload);
  }
}
